import { useEffect, useMemo, useRef, useState } from "react";
import { dataCurta, dataPorExtenso, diaDaSemana, diasEntre, somarDias, type DiaDoHeatmap } from "@noesis/shared";
import { SeletorDeDia } from "./SeletorDeDia.js";

/**
 * Heatmap de doze meses — o caso difícil da RN-30.
 *
 * **Uma grade só, idêntica no celular e no PC.** Nada é removido nem resumido
 * por largura: os mesmos 365 dias aparecem nos dois.
 *
 * **A janela é fixa em doze meses, e isso é decisão fechada.** Houve uma versão
 * com janela adaptativa, que abria em oito semanas e crescia conforme o
 * histórico aparecia; ela foi revertida. O que a motivou era o cartão parecer
 * vazio, e o problema não era o dado: uma grade de oito colunas boiando no meio
 * de um cartão de largura inteira fica **pior** que doze meses honestos. Dia sem
 * estudo é parte da prova histórica, não ruído em volta dela — é ele que dá
 * escala aos dias que têm. Ver a seção 9 de `docs/identidade-visual.md`.
 *
 * O que muda é o espaço: no celular a grade **rola dentro da própria caixa**, já
 * aberta no fim com hoje visível; no PC ela ocupa a largura inteira da página e
 * **não rola** — as 53 semanas cabem, com os rótulos de mês por cima. Grade que
 * rola de lado é solução para tela apertada, não para 1900px, e herdar essa
 * restrição no desktop era o layout do celular esticado que a RN-30 proíbe.
 *
 * A página nunca rola de lado — a rolagem, quando existe, é da caixa (ADR 006).
 *
 * **Acessibilidade sem encolher o dado.** A célula fica muito abaixo dos 44px de
 * alvo, então tocar uma célula é **atalho, não caminho**: ao lado há um seletor
 * de dia com alvo cheio que alcança a faixa inteira e abre o mesmo detalhe. É
 * `<table>` de verdade, com cabeçalho de dia da semana, para leitor de tela
 * percorrer os mesmos números que o olho vê.
 *
 * **A faixa comprimiu** (ADR 029). Ela era o maior bloco da tela de início e
 * gastava essa área mostrando onze meses vazios. O dado não mudou — a janela de
 * doze meses continua sendo decisão fechada, e dia sem estudo é parte da prova
 * histórica —, mas a célula, o vão e o rodapé encolheram, e o cartão passou de
 * ~300px para ~210px. O que ela precisa provar é constância, e constância se
 * prova numa faixa.
 */

const DIAS_DA_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/** Cinco faixas de intensidade. O grau vira classe, nunca cor literal no JSX. */
function grau(dia: DiaDoHeatmap | undefined, meta: number): 0 | 1 | 2 | 3 | 4 {
  if (!dia || dia.respondidos === 0) return 0;
  if (dia.cumpriu) return 4;
  const fracao = dia.respondidos / Math.max(meta, 1);
  if (fracao >= 0.66) return 3;
  if (fracao >= 0.33) return 2;
  return 1;
}

/** O domingo da semana de uma data — a grade começa alinhada por semana. */
function domingoDe(data: string): string {
  return somarDias(data, -diaDaSemana(data));
}

export function Heatmap({
  dias,
  de,
  hoje,
  meta,
  diasDeDescanso,
}: {
  dias: DiaDoHeatmap[];
  de: string;
  hoje: string;
  meta: number;
  /**
   * Os dias da semana de descanso da trilha (RN-17). Vem da trilha, e não de
   * cada dia da lista, porque **dia de descanso sem estudo não tem linha no
   * diário**: sem isto, o buraco do fim de semana pareceria falha, que é
   * exatamente o que a regra diz que ele não é.
   */
  diasDeDescanso: number[];
}) {
  const rolagem = useRef<HTMLDivElement | null>(null);
  const [escolhido, setEscolhido] = useState<string>(hoje);

  const porData = useMemo(() => new Map(dias.map((d) => [d.data, d])), [dias]);

  const descanso = useMemo(() => new Set(diasDeDescanso), [diasDeDescanso]);
  const ehDescanso = (data: string): boolean => descanso.has(diaDaSemana(data));

  const semanas = useMemo(() => {
    const inicio = domingoDe(de);
    const total = Math.floor(diasEntre(inicio, hoje) / 7) + 1;
    return Array.from({ length: total }, (_, s) => {
      const domingo = somarDias(inicio, s * 7);
      return Array.from({ length: 7 }, (_, d) => somarDias(domingo, d));
    });
  }, [de, hoje]);

  // Abre no fim: hoje é o que importa ver primeiro, e rolar até ele a cada
  // visita seria pedágio.
  useEffect(() => {
    const el = rolagem.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [semanas]);

  /**
   * Onde cada mês começa, para o cabeçalho da grade.
   *
   * Um rótulo por bloco de semanas do mesmo mês. Blocos de uma semana só ficam
   * sem rótulo: "ago" sobre uma coluna de 14px não é legível, e a alternativa —
   * girar o texto — seria pior de ler do que não ter.
   */
  const meses = useMemo(() => {
    const blocos: { rotulo: string; semanas: number }[] = [];
    for (const semana of semanas) {
      // O mês da coluna é o da quinta-feira: a semana que atravessa a virada
      // pertence visualmente ao mês em que está a maior parte dela.
      const mes = Number(semana[4]!.slice(5, 7));
      const ultimo = blocos[blocos.length - 1];
      if (ultimo && ultimo.rotulo === MESES[mes - 1]) ultimo.semanas += 1;
      else blocos.push({ rotulo: MESES[mes - 1]!, semanas: 1 });
    }
    return blocos;
  }, [semanas]);

  const detalhe = porData.get(escolhido);
  const total = dias.reduce((s, d) => s + d.respondidos, 0);
  const diasComEstudo = dias.filter((d) => d.respondidos > 0).length;

  return (
    <section className="heatmap" aria-labelledby="heatmap-titulo">
      <div className="heatmap__topo">
        <h2 className="t-secao" id="heatmap-titulo">
          Atividade
        </h2>
        {/*
          O contador é **legenda, e não etiqueta**: ele é uma frase com dois
          números dentro, e frase em caixa alta com entreletra se lê letra a
          letra. A etiqueta abre um bloco e nomeia o que vem depois; esta linha
          diz um valor, e valor não é rótulo.

          E o plural é contado, não presumido. Ela imprimia "3 respostas em 1
          dias" — o `-s` fixo que o `plural` do `@noesis/shared` existe para
          acabar. O número fica fora da função porque ele é `.dado`, e a função
          devolve número e palavra juntos.
        */}
        <p className="t-anotacao-p">
          <span className="dado">{total}</span> {total === 1 ? "resposta" : "respostas"} em{" "}
          <span className="dado">{diasComEstudo}</span> {diasComEstudo === 1 ? "dia" : "dias"}
        </p>
      </div>

      <div className="heatmap__rolagem cartao-mao cartao-mao--espelho" ref={rolagem}>
        <table className="heatmap__grade">
          <caption className="so-leitor">
            Respostas por dia nos últimos doze meses, uma coluna por semana.
          </caption>
          <thead>
            {/* Rótulos de mês, visíveis. Ficam fora do fluxo do leitor de tela
                porque a linha de baixo já nomeia cada semana por data. */}
            <tr className="heatmap__meses" aria-hidden="true">
              <td />
              {meses.map((m, i) => (
                <td key={`${m.rotulo}-${i}`} colSpan={m.semanas} className="heatmap__mes">
                  {m.semanas > 1 ? m.rotulo : ""}
                </td>
              ))}
            </tr>
            <tr className="so-leitor">
              <th scope="col">Dia da semana</th>
              {semanas.map((semana) => (
                <th key={semana[0]} scope="col">
                  Semana de {dataCurta(semana[0]!)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DIAS_DA_SEMANA.map((nome, linha) => (
              <tr key={nome}>
                {/* Só as ímpares aparecem: sete rótulos em 12px viram borrão.
                    O leitor de tela recebe os sete, porque `aria-hidden` está
                    só no texto, não na célula. */}
                <th scope="row" className="heatmap__dia-semana">
                  <span className={linha % 2 === 1 ? undefined : "so-leitor"}>{nome}</span>
                </th>
                {semanas.map((semana) => {
                  const data = semana[linha]!;
                  const d = porData.get(data);
                  const foraDaFaixa = data > hoje || data < de;
                  return (
                    <td key={data} className="heatmap__celula-caixa">
                      {foraDaFaixa ? (
                        <span className="heatmap__celula heatmap__celula--fora" aria-hidden="true" />
                      ) : (
                        <button
                          type="button"
                          className={`heatmap__celula heatmap__celula--g${grau(d, meta)}${d?.ferias ? " heatmap__celula--ferias" : ehDescanso(data) ? " heatmap__celula--descanso" : ""}${data === escolhido ? " heatmap__celula--escolhida" : ""}`}
                          onClick={() => setEscolhido(data)}
                          aria-label={`${dataCurta(data)}: ${d?.respondidos ?? 0} respostas${d?.cumpriu ? ", meta cumprida" : ""}${d?.ferias ? ", em férias" : ehDescanso(data) ? ", dia de descanso" : ""}`}
                          /*
                           * A isenção do mínimo de alvo, declarada aqui e não
                           * numa lista dentro da ferramenta de medida: são 365
                           * células de doze meses, e 365 alvos de 44px não
                           * cabem em tela nenhuma. Ela **vale enquanto** o
                           * seletor de data abaixo alcançar o mesmo dia — se
                           * ele sair, este atributo sai junto e as 365 voltam a
                           * ser violação. SPEC, RN-56.
                           */
                          data-alvo-isento="heatmap: o mesmo dia é alcançável pelo seletor de data (RN-56)"
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="heatmap__rodape">
        {/*
          O alvo cheio que a célula não pode ser. Toda data da faixa é alcançável
          por aqui, sem depender de acertar um quadradinho — e é essa alcance que
          sustenta a isenção de alvo das 365 células (RN-56).

          Era um `<input type="date">`, e o calendário do navegador é desenhado
          pelo sistema operacional: ele ignorava tema, tipografia e superfície, e
          imprimia a data no formato do locale em vez do formatador único do app.
          ADR 029.
        */}
        <SeletorDeDia valor={escolhido} de={de} ate={hoje} aoEscolher={setEscolhido} />

        <ul className="heatmap__legenda" aria-hidden="true">
          <li className="heatmap__legenda-texto">Menos</li>
          {[0, 1, 2, 3, 4].map((g) => (
            <li key={g} className={`heatmap__celula heatmap__celula--g${g}`} />
          ))}
          <li className="heatmap__legenda-texto">Mais</li>
          {/*
            A marca do descanso entra na legenda com o nome ao lado: ao
            contrário das cinco faixas, que se leem pela ordem, um anel sozinho
            não se explica. RN-17.
          */}
          <li className="heatmap__celula heatmap__celula--descanso" />
          <li className="heatmap__legenda-texto">Descanso</li>
        </ul>
      </div>

      <p className="heatmap__detalhe t-ajuda" aria-live="polite">
        <span className="dado">{dataPorExtenso(escolhido)}</span>
        {" — "}
        {detalhe
          ? `${detalhe.respondidos} respostas, ${detalhe.acertos} certas${detalhe.cumpriu ? " · meta cumprida" : ""}${detalhe.ferias ? " · em férias" : ehDescanso(escolhido) ? " · dia de descanso" : ""}`
          : ehDescanso(escolhido)
            ? "dia de descanso"
            : "sem estudo neste dia"}
      </p>
    </section>
  );
}
